import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CpmButton from '../../components/design-system/Button';
import CpmErrorCard from '../../components/design-system/ErrorCard';
import CpmInlineErrorBanner from '../../components/design-system/InlineErrorBanner';
import CpmModal from '../../components/design-system/Modal';
import CpmToggleSwitch from '../../components/design-system/ToggleSwitch';
import { listStates } from '../../services/productMasterService';
import {
    createStarter,
    getStarterCatalogue,
    getStarterDetails,
    starterSaveError,
    updateStarter,
} from '../../services/starterService';
import type { CpmState } from '../../types/centralProductMaster.types';
import type { CataloguePermission, StarterDetail } from '../../types/accessManagement.types';
import PermissionRail from './components/PermissionRail';
import StateMultiSelect from './components/StateMultiSelect';
import { STARTER_COPY } from './starterCopy';

interface Draft {
    name: string;
    description: string;
    stateIds: string[];
    signInDashboard: boolean;
    signInPos: boolean;
    permissionIds: string[];
}

const EMPTY_DRAFT: Draft = {
    name: '',
    description: '',
    stateIds: [],
    signInDashboard: false,
    signInPos: false,
    permissionIds: [],
};

function sameIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) return false;
    const a = [...left].sort();
    const b = [...right].sort();
    return a.every((id, index) => id === b[index]);
}

function isSame(left: Draft, right: Draft): boolean {
    return (
        left.name === right.name &&
        left.description === right.description &&
        left.signInDashboard === right.signInDashboard &&
        left.signInPos === right.signInPos &&
        sameIds(left.stateIds, right.stateIds) &&
        sameIds(left.permissionIds, right.permissionIds)
    );
}

function stateLabel(state: CpmState): string {
    return state.name || state.state || state._id;
}

function draftFromDetail(row: StarterDetail, known: Set<string> | null): Draft {
    const permissionIds = row.permissionIds || [];
    return {
        name: row.name || '',
        description: row.description || '',
        stateIds: (row.stateIds || []).map((id) => String(id)),
        signInDashboard: row.signInDashboard === true,
        signInPos: row.signInPos === true,
        permissionIds:
            known && known.size
                ? permissionIds.filter((id) => known.has(id))
                : permissionIds.filter((id) => typeof id === 'string'),
    };
}

function BackArrow() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
            <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default function StarterEditorPage() {
    const navigate = useNavigate();
    const params = useParams();
    const routeId = params.id || 'new';
    const isNew = routeId === 'new';
    const skipLoad = useRef(false);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const typedNameRef = useRef('');
    const typedForRoute = useRef('');
    const saveInFlight = useRef(false);
    const detailsGeneration = useRef(0);
    const savedOnRoute = useRef('');

    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [savedName, setSavedName] = useState('');
    const [baseline, setBaseline] = useState<Draft>(EMPTY_DRAFT);
    const [permissions, setPermissions] = useState<CataloguePermission[]>([]);
    const [states, setStates] = useState<CpmState[]>([]);
    const [stateOpen, setStateOpen] = useState(false);
    const [detailsState, setDetailsState] = useState<'loading' | 'ready' | 'error'>(
        isNew ? 'ready' : 'loading'
    );
    const [catalogueError, setCatalogueError] = useState(false);
    const [statesError, setStatesError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [nameError, setNameError] = useState('');
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [detailsAttempt, setDetailsAttempt] = useState(0);
    const [catalogueAttempt, setCatalogueAttempt] = useState(0);
    const [statesAttempt, setStatesAttempt] = useState(0);

    useEffect(() => {
        if (skipLoad.current) {
            skipLoad.current = false;
            return;
        }
        if (isNew) {
            typedNameRef.current = '';
            typedForRoute.current = '';
            savedOnRoute.current = '';
            setDraft(EMPTY_DRAFT);
            setBaseline(EMPTY_DRAFT);
            setSavedName('');
            setDetailsState('ready');
            setSaved(false);
            setSaveError('');
            setNameError('');
            return;
        }
        const generationAtStart = detailsGeneration.current;
        let cancelled = false;
        const load = async () => {
            setDetailsState((current) => (current === 'ready' ? current : 'loading'));
            try {
                const row = await getStarterDetails(routeId);
                if (cancelled) return;
                if (generationAtStart !== detailsGeneration.current || savedOnRoute.current === routeId) {
                    setDetailsState((current) => (current === 'loading' ? 'ready' : current));
                    return;
                }
                const next = draftFromDetail(row, null);
                const detailsName = next.name || '';
                const typedHere = typedForRoute.current === routeId ? typedNameRef.current : '';
                const keepTypedName =
                    (saveInFlight.current && typedForRoute.current === routeId) ||
                    (typedHere.trim() !== '' && typedHere !== detailsName);
                if (keepTypedName) {
                    const keptName = typedHere.trim() !== '' && typedHere !== detailsName ? typedHere : '';
                    setDraft((current) => ({ ...next, name: keptName || current.name }));
                    setBaseline((current) => ({ ...next, name: current.name }));
                    setDetailsState('ready');
                    return;
                }
                detailsGeneration.current += 1;
                typedNameRef.current = next.name;
                typedForRoute.current = routeId;
                setDraft(next);
                setBaseline(next);
                setSavedName(next.name);
                setDetailsState('ready');
            } catch {
                if (!cancelled) setDetailsState('error');
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [isNew, routeId, detailsAttempt]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const catalogue = await getStarterCatalogue();
                if (cancelled) return;
                setPermissions(catalogue?.permissions || []);
                setCatalogueError(false);
            } catch {
                if (!cancelled) setCatalogueError(true);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [catalogueAttempt]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const rows = await listStates();
                if (cancelled) return;
                setStates(rows);
                setStatesError(false);
            } catch {
                if (!cancelled) setStatesError(true);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [statesAttempt]);

    useEffect(() => {
        if (!permissions.length) return;
        const known = new Set(permissions.map((permission) => permission.id));
        const strip = (current: Draft): Draft => ({
            ...current,
            permissionIds: current.permissionIds.filter((id) => known.has(id)),
        });
        setDraft((current) => strip(current));
        setBaseline((current) => strip(current));
    }, [permissions]);

    const dirty = useMemo(() => !isSame(draft, baseline), [draft, baseline]);

    useEffect(() => {
        if (dirty) setSaved(false);
    }, [dirty]);

    const stateOptions = states.map((state) => ({ id: state._id, name: stateLabel(state) }));
    const heading = savedName.trim() ? savedName : STARTER_COPY.NEW_STARTER;

    const goBack = () => {
        if (saving || dirty) {
            setLeaveOpen(true);
            return;
        }
        navigate('/starters');
    };

    const save = async () => {
        const liveName = nameInputRef.current ? nameInputRef.current.value : draft.name;
        const remembered = typedNameRef.current;
        const fieldName =
            remembered.trim() && remembered !== liveName && liveName.trim() === savedName.trim()
                ? remembered
                : liveName;
        const name = fieldName.trim();
        if (!name) {
            setNameError(STARTER_COPY.NAME_REQUIRED);
            setSaveError('');
            return;
        }
        if (!permissions.length) {
            setSaveError(STARTER_COPY.CATALOGUE_ERROR);
            return;
        }
        const known = new Set(permissions.map((permission) => permission.id));
        const body = {
            name,
            description: draft.description.trim(),
            permissionIds: draft.permissionIds.filter((id) => known.has(id)),
            signInDashboard: draft.signInDashboard,
            signInPos: draft.signInPos,
            stateIds: draft.stateIds,
        };
        if (fieldName !== draft.name) {
            setDraft((current) => ({ ...current, name: fieldName }));
        }
        detailsGeneration.current += 1;
        saveInFlight.current = true;
        setSaving(true);
        setSaveError('');
        setNameError('');
        try {
            const savedRow = isNew
                ? await createStarter(body)
                : await updateStarter({ ...body, templateId: routeId });
            const appliedName = name;
            let next: Draft;
            try {
                next = savedRow
                    ? draftFromDetail(savedRow, known)
                    : {
                          name: appliedName,
                          description: body.description,
                          permissionIds: body.permissionIds,
                          signInDashboard: body.signInDashboard,
                          signInPos: body.signInPos,
                          stateIds: body.stateIds,
                      };
            } catch {
                next = {
                    name: appliedName,
                    description: body.description,
                    permissionIds: body.permissionIds,
                    signInDashboard: body.signInDashboard,
                    signInPos: body.signInPos,
                    stateIds: body.stateIds,
                };
            }
            next.name = appliedName;
            detailsGeneration.current += 1;
            typedNameRef.current = appliedName;
            typedForRoute.current = isNew && savedRow && savedRow._id ? String(savedRow._id) : routeId;
            savedOnRoute.current = typedForRoute.current;
            setDraft(next);
            setBaseline(next);
            setSavedName(appliedName);
            setSaved(true);
            if (isNew && savedRow && savedRow._id) {
                skipLoad.current = true;
                navigate(`/starters/${savedRow._id}`, { replace: true });
            }
        } catch (err) {
            const parsed = starterSaveError(err);
            if (parsed.nameClash) {
                setNameError(STARTER_COPY.NAME_EXISTS);
                setSaveError('');
            } else {
                setSaveError(parsed.message);
            }
        } finally {
            saveInFlight.current = false;
            setSaving(false);
        }
    };

    if (detailsState === 'loading') {
        return (
            <div className="cpm-theme" style={{ padding: '26px 34px', background: '#F5F5F8', minHeight: '100vh' }}>
                <CpmButton variant="ghost" size="sm" onClick={() => navigate('/starters')} leadingIcon={<BackArrow />}>
                    {STARTER_COPY.BACK}
                </CpmButton>
            </div>
        );
    }

    if (detailsState === 'error') {
        return (
            <div className="cpm-theme" style={{ padding: '26px 34px', background: '#F5F5F8', minHeight: '100vh' }}>
                <CpmButton variant="ghost" size="sm" onClick={() => navigate('/starters')} leadingIcon={<BackArrow />}>
                    {STARTER_COPY.BACK}
                </CpmButton>
                <div style={{ marginTop: 16 }}>
                    <CpmErrorCard
                        title={STARTER_COPY.DETAILS_ERROR}
                        body={STARTER_COPY.ERROR_BODY}
                        onRetry={() => setDetailsAttempt((key) => key + 1)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className="cpm-theme flex flex-col h-screen overflow-hidden"
            style={{ background: '#F5F5F8' }}
        >
            <div className="min-h-0 flex-1 overflow-y-auto" style={{ padding: '26px 34px 0' }}>
                <div
                    style={{
                        maxWidth: 1440,
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14,
                    }}
                >
                    <div className="flex items-center justify-between flex-wrap" style={{ gap: 24 }}>
                        <div className="flex items-center" style={{ gap: 14, minWidth: 0 }}>
                            <CpmButton variant="ghost" size="sm" onClick={goBack} leadingIcon={<BackArrow />}>
                                {STARTER_COPY.BACK}
                            </CpmButton>
                            <h1
                                style={{
                                    margin: 0,
                                    fontSize: 25,
                                    fontWeight: 800,
                                    letterSpacing: '-0.022em',
                                    lineHeight: 1.1,
                                    minWidth: 0,
                                }}
                            >
                                {heading}
                            </h1>
                        </div>
                        <span
                            style={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                                lineHeight: 1.4,
                                maxWidth: 400,
                            }}
                        >
                            {STARTER_COPY.COPY_LINE}
                        </span>
                    </div>

                    {saved ? (
                        <div
                            style={{
                                padding: '12px 14px',
                                borderRadius: 12,
                                background: 'var(--cpm-success-bg, #ecfdf3)',
                                border: '1px solid var(--cpm-success-border, #abefc6)',
                            }}
                        >
                            <div style={{ fontSize: 13.5, fontWeight: 800 }}>{STARTER_COPY.SAVED_TITLE}</div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                                {STARTER_COPY.SAVED_BODY}
                            </div>
                        </div>
                    ) : null}

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(360px, 0.82fr) minmax(0, 1.7fr)',
                            gap: 14,
                            alignItems: 'start',
                        }}
                    >
                        <div
                            style={{
                                position: 'sticky',
                                top: 20,
                                background: '#fff',
                                border: '1px solid var(--cpm-border-default)',
                                borderRadius: 16,
                                boxShadow: 'var(--cpm-shadow-card)',
                                padding: 20,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 20,
                            }}
                        >
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span className="flex items-center justify-between">
                                    <span style={{ fontSize: 12.5, fontWeight: 700 }}>{STARTER_COPY.NAME}</span>
                                    <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--cpm-text-secondary)' }}>
                                        {STARTER_COPY.REQUIRED}
                                    </span>
                                </span>
                                <input
                                    ref={nameInputRef}
                                    value={draft.name}
                                    placeholder={STARTER_COPY.NAME_PLACEHOLDER}
                                    onChange={(event) => {
                                        typedNameRef.current = event.target.value;
                                        typedForRoute.current = routeId;
                                        detailsGeneration.current += 1;
                                        setDraft((current) => ({ ...current, name: event.target.value }));
                                        setNameError('');
                                    }}
                                    style={{
                                        height: 42,
                                        borderRadius: 10,
                                        border: `1.5px solid ${nameError ? 'var(--cpm-error)' : 'var(--cpm-border-strong)'}`,
                                        padding: '0 12px',
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        outline: 'none',
                                    }}
                                />
                                {nameError ? (
                                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cpm-error)' }}>
                                        {nameError}
                                    </span>
                                ) : null}
                            </label>

                            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span className="flex items-center justify-between">
                                    <span style={{ fontSize: 12.5, fontWeight: 700 }}>{STARTER_COPY.DESCRIPTION}</span>
                                    <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--cpm-text-secondary)' }}>
                                        {STARTER_COPY.OPTIONAL}
                                    </span>
                                </span>
                                <textarea
                                    value={draft.description}
                                    rows={3}
                                    placeholder={STARTER_COPY.DESC_PLACEHOLDER}
                                    onChange={(event) =>
                                        setDraft((current) => ({
                                            ...current,
                                            description: event.target.value,
                                        }))
                                    }
                                    style={{
                                        borderRadius: 10,
                                        border: '1.5px solid var(--cpm-border-strong)',
                                        padding: '10px 12px',
                                        fontSize: 13.5,
                                        fontWeight: 600,
                                        resize: 'vertical',
                                        outline: 'none',
                                    }}
                                />
                            </label>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{STARTER_COPY.STATE}</span>
                                {statesError ? (
                                    <CpmInlineErrorBanner
                                        compact
                                        message={STARTER_COPY.STATES_ERROR}
                                        onRetry={() => setStatesAttempt((key) => key + 1)}
                                    />
                                ) : null}
                                <StateMultiSelect
                                    mode="editor"
                                    options={stateOptions}
                                    selectedIds={draft.stateIds}
                                    onChange={(stateIds) => setDraft((current) => ({ ...current, stateIds }))}
                                    open={stateOpen}
                                    onOpenChange={setStateOpen}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{STARTER_COPY.SIGN_IN}</span>
                                <div style={{ border: '1px solid var(--cpm-border-default)', borderRadius: 12 }}>
                                    <SignRow
                                        title={STARTER_COPY.DASHBOARD}
                                        description={STARTER_COPY.DASH_HELP}
                                        checked={draft.signInDashboard}
                                        onChange={() =>
                                            setDraft((current) => ({
                                                ...current,
                                                signInDashboard: !current.signInDashboard,
                                            }))
                                        }
                                    />
                                    <div style={{ height: 1, background: 'var(--cpm-border-default)' }} />
                                    <SignRow
                                        title={STARTER_COPY.POS}
                                        description={STARTER_COPY.POS_HELP}
                                        checked={draft.signInPos}
                                        onChange={() =>
                                            setDraft((current) => ({
                                                ...current,
                                                signInPos: !current.signInPos,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        {catalogueError ? (
                            <CpmErrorCard
                                title={STARTER_COPY.CATALOGUE_ERROR}
                                body={STARTER_COPY.ERROR_BODY}
                                onRetry={() => setCatalogueAttempt((key) => key + 1)}
                            />
                        ) : (
                            <PermissionRail
                                permissions={permissions}
                                selectedIds={draft.permissionIds}
                                onChange={(permissionIds) =>
                                    setDraft((current) => ({ ...current, permissionIds }))
                                }
                            />
                        )}
                    </div>
                </div>
            </div>

            <div
                style={{
                    flex: 'none',
                    padding: '14px 34px',
                    background: 'rgba(249,249,252,0.88)',
                    backdropFilter: 'blur(14px)',
                    borderTop: '1.5px solid var(--cpm-border-strong)',
                    boxShadow: '0 -8px 24px -12px rgba(14,8,88,.14)',
                }}
            >
                <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {saveError ? (
                        <CpmInlineErrorBanner
                            message={saveError}
                            onRetry={() => {
                                void save();
                            }}
                        >
                            {saveError === STARTER_COPY.CATALOGUE_ERROR ? null : STARTER_COPY.SAVE_FAILED_BODY}
                        </CpmInlineErrorBanner>
                    ) : null}
                    <div className="flex items-center justify-end" style={{ gap: 12 }}>
                        {saving ? (
                            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cpm-text-secondary)' }}>
                                {STARTER_COPY.SAVING}
                            </span>
                        ) : null}
                        <CpmButton variant="navy" size="md" disabled={saving || detailsState !== 'ready'} onClick={() => void save()}>
                            {STARTER_COPY.SAVE}
                        </CpmButton>
                    </div>
                </div>
            </div>

            <CpmModal
                open={leaveOpen}
                onClose={() => {
                    if (!saving) setLeaveOpen(false);
                }}
                maxWidth={500}
                closeOnEscape={!saving}
                closeOnBackdrop={!saving}
            >
                <div className="flex flex-col" style={{ padding: '22px 22px 18px', gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>
                        {STARTER_COPY.LEAVE_TITLE}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.55, color: 'var(--cpm-text-secondary)' }}>
                        {STARTER_COPY.LEAVE_BODY}
                    </div>
                    <div className="flex flex-wrap items-center justify-end" style={{ gap: 9, marginTop: 6 }}>
                        <CpmButton
                            variant="ghost"
                            size="md"
                            disabled={saving}
                            onClick={() => navigate('/starters')}
                        >
                            {STARTER_COPY.LEAVE_DISCARD}
                        </CpmButton>
                        <CpmButton variant="navy" size="md" disabled={saving} onClick={() => setLeaveOpen(false)}>
                            {STARTER_COPY.LEAVE_STAY}
                        </CpmButton>
                    </div>
                </div>
            </CpmModal>
        </div>
    );
}

function SignRow({
    title,
    description,
    checked,
    onChange,
}: {
    title: string;
    description: string;
    checked: boolean;
    onChange: () => void;
}) {
    return (
        <div className="flex items-center justify-between" style={{ gap: 12, padding: '12px 14px' }}>
            <div>
                <div style={{ fontSize: 13.5, fontWeight: 800 }}>{title}</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>{description}</div>
            </div>
            <CpmToggleSwitch size="field" checked={checked} onChange={onChange} aria-label={title} />
        </div>
    );
}
